import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, Building2, Link as LinkIcon, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({
  company: "", email: "", name: "", primary_contact: "",
  category: "fabrication", project_id: "", bom_item_ids: [], note: "",
});

const CATEGORIES = ["fabrication", "3d_printing", "electronics", "tooling", "logistics", "design", "consulting", "other"];

export default function VendorInviteWizard({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(blank);
  const [projects, setProjects] = useState([]);
  const [bom, setBom] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(blank()); setResult(null); setCopied(false);
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    setBom([]);
    if (!form.project_id) return;
    api.get(`/projects/${form.project_id}/hw/bom`).then(({ data }) => setBom(data || [])).catch(() => {});
    setForm((f) => ({ ...f, bom_item_ids: [] }));
  }, [form.project_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBom = (id) => set("bom_item_ids", form.bom_item_ids.includes(id) ? form.bom_item_ids.filter((x) => x !== id) : [...form.bom_item_ids, id]);

  const submit = async () => {
    if (!form.company.trim() || !form.email.trim()) { toast.error("Company + email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/vendors/invite", {
        ...form,
        project_id: form.project_id || null,
        bom_item_ids: form.bom_item_ids.length ? form.bom_item_ids : null,
      });
      toast.success(`Invite sent to ${data.email}`);
      setResult(data);
      onCreated && onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const copy = () => {
    if (!result?.invitation?.link) return;
    navigator.clipboard.writeText(result.invitation.link);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl" data-testid="vendor-invite-wizard">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Building2 size={16} /> Onboard vendor</DialogTitle>
          <DialogDescription>Send a magic-link invite with minimal info. They&apos;ll self-complete the rest (GST, bank, NDA), then you approve.</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label className="label-overline">Company *</Label><Input className="rounded-sm mt-1" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Manufacturing Co" data-testid="vinv-company" /></div>
              <div><Label className="label-overline">Primary contact email *</Label><Input className="rounded-sm mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="contact@acme.com" data-testid="vinv-email" /></div>
              <div><Label className="label-overline">Contact name</Label><Input className="rounded-sm mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="(optional)" data-testid="vinv-name" /></div>
              <div className="sm:col-span-2">
                <Label className="label-overline">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="vinv-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label className="label-overline">Pre-scope to project (optional)</Label>
                <Select value={form.project_id || "__none"} onValueChange={(v) => set("project_id", v === "__none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="vinv-project"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    {projects.filter((p) => p.physical_product).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.business_unit}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {form.project_id && (
                <div className="sm:col-span-2">
                  <Label className="label-overline">Share BOM lines (optional)</Label>
                  {bom.length === 0 ? (
                    <div className="text-[11px] text-slate-400 mt-1">No BOM items on this project yet.</div>
                  ) : (
                    <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1 mt-1">
                      {bom.map((b) => (
                        <label key={b.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border-2 ${form.bom_item_ids.includes(b.id) ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200"}`}>
                          <input type="checkbox" checked={form.bom_item_ids.includes(b.id)} onChange={() => toggleBom(b.id)} data-testid={`vinv-bom-${b.id}`} />
                          <span className="text-[11px]">{b.part_name} · qty {b.qty}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="sm:col-span-2">
                <Label className="label-overline">Note (visible to vendor)</Label>
                <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Anything you want them to know before they fill in their details…" />
              </div>
            </div>

            <DialogFooter>
              <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vinv-submit">
                {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />}Send invite
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="card-flat p-3 bg-emerald-50 border-l-4 border-l-emerald-500">
              <div className="text-xs font-display font-bold text-slate-900 mb-1">Invite sent to {result.email}</div>
              <p className="text-[11px] text-slate-600">The vendor will fill in their own details, sign the NDA and submit for your approval. Status starts as <b>invited</b>.</p>
            </div>
            <div className="card-flat p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><LinkIcon size={11} /> Magic-link backup</div>
              <div className="flex items-center gap-2">
                <Input readOnly className="rounded-sm text-[10px] font-mono" value={result.invitation?.link || ""} />
                <Button size="sm" variant="outline" onClick={copy} className="rounded-sm" data-testid="vinv-copy">
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">Valid for 7 days. Use this if email delivery is delayed.</p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
